import { breakWasabiUrlForDebug } from './debug'
import { getCachedImageBlobUrl } from './storage'
import type { ImageDisplaySource, ImageUrlFields, UploadResponseLike } from './types'

function pickUrlFields(source?: ImageDisplaySource | null): ImageUrlFields {
  if (!source) return {}
  const fromResponse = { ...source.res, ...source.uploadResponse }
  return {
    imageUrl: fromResponse.imageUrl ?? source.uploadedUrl,
    imageUrlV1: fromResponse.imageUrlV1 ?? source.uploadedUrlV1,
    thumbnailUrl: fromResponse.thumbnailUrl ?? source.thumbnailUrl,
    thumbnailUrlV1: fromResponse.thumbnailUrlV1 ?? source.thumbnailUrlV1,
    showImageUrl: fromResponse.showImageUrl ?? source.showImageUrl
  }
}

function isBlobUrl(url?: string): boolean {
  return !!url && (url.startsWith('blob:') || url.startsWith('data:'))
}

const WASABI_HOST_PATTERN = /wasabisys\.com/i

function isWasabiUrl(url?: string): boolean {
  return !!url && WASABI_HOST_PATTERN.test(url)
}

function isSignedUrl(url: string): boolean {
  return /[?&]X-Amz-/i.test(url) || /[?&]x-amz-/i.test(url)
}

/** 同 tier 内优先带签名 URL（浏览器可直链），否则取第一个有效值 */
function pickBestUrl(candidates: (string | undefined)[]): string {
  const valid = candidates.filter((u): u is string => !!u)
  const signed = valid.find(isSignedUrl)
  if (signed) return signed
  return valid[0] || ''
}

/** Wasabi 层：showImageUrl(仅 wasabi 域名) > thumbnailUrlV1 > imageUrlV1 */
function resolveWasabiDisplayUrl(urls: ImageUrlFields): string {
  const url = pickBestUrl([
    isWasabiUrl(urls.showImageUrl) ? urls.showImageUrl : undefined,
    urls.thumbnailUrlV1,
    urls.imageUrlV1
  ])
  return url ? breakWasabiUrlForDebug(url) : ''
}

/** S3 层：showImageUrl(非 wasabi) > thumbnailUrl > imageUrl */
function resolveS3DisplayUrl(urls: ImageUrlFields): string {
  return pickBestUrl([
    urls.showImageUrl && !isWasabiUrl(urls.showImageUrl) ? urls.showImageUrl : undefined,
    urls.thumbnailUrl,
    urls.imageUrl
  ])
}

/**
 * 远端预览优先级（严格 Wasabi > S3）：
 * - Wasabi：imageUrlV1 / thumbnailUrlV1 / showImageUrl(wasabisys.com)
 * - S3：imageUrl / thumbnailUrl / showImageUrl(其他域名)
 */
export function resolveRemoteDisplayUrl(source?: ImageDisplaySource | null): string {
  if (!source) return ''

  const urls = pickUrlFields(source)
  const wasabiUrl = resolveWasabiDisplayUrl(urls)
  const s3Url = resolveS3DisplayUrl(urls)

  if (wasabiUrl) return wasabiUrl
  if (s3Url) return s3Url

  // 兜底：按 previewUrl / originalUrl 域名判断 tier
  const previewFallback = source.previewUrl || source.originalUrl || ''
  if (isWasabiUrl(previewFallback)) return breakWasabiUrlForDebug(previewFallback)

  return previewFallback || s3Url
}

/** 仅 S3 层 URL，用于 Wasabi 加载失败时的 fallback */
export function resolveS3FallbackDisplayUrl(source?: ImageDisplaySource | null): string {
  if (!source) return ''
  const urls = pickUrlFields(source)
  return resolveS3DisplayUrl(urls)
}

/** 提交任务用的单 URL 兜底：Wasabi > S3 > showImageUrl（不使用本地） */
export function resolveSubmitImageUrl(source?: ImageDisplaySource | null): string {
  const urls = pickUrlFields(source)
  return urls.imageUrlV1 || urls.imageUrl || urls.showImageUrl || ''
}

/** 提交任务用的结构化 URL（保留 S3 / Wasabi 分字段） */
export function resolveSubmitImageUrls(source?: ImageDisplaySource | null): ImageUrlFields {
  const urls = pickUrlFields(source)
  return {
    imageUrl: urls.imageUrl || '',
    imageUrlV1: urls.imageUrlV1 || '',
    thumbnailUrl: urls.thumbnailUrl || urls.imageUrl || '',
    thumbnailUrlV1: urls.thumbnailUrlV1 || urls.imageUrlV1 || '',
    showImageUrl: urls.showImageUrl || urls.imageUrlV1 || urls.imageUrl || ''
  }
}

export function toImageUrlFields(response: UploadResponseLike): ImageUrlFields {
  return {
    imageUrl: response.imageUrl,
    imageUrlV1: response.imageUrlV1,
    thumbnailUrl: response.thumbnailUrl,
    thumbnailUrlV1: response.thumbnailUrlV1,
    showImageUrl: response.showImageUrl
  }
}

/** 任务封面 / 历史缩略图：远端 Wasabi > S3 > ... */
export function resolveTaskCoverUrl(
  task?: {
    thumbnail?: string
    thumbnailV1?: string
    cover?: string
    data?: {
      thumbnail?: string
      thumbnailV1?: string
      uploadSoraUrl?: string
      imageUrl?: string
      imageUrlV1?: string
    } | null
  } | null
): string {
  if (!task) return ''

  const remote = resolveRemoteDisplayUrl({
    res: {
      imageUrlV1: task.data?.imageUrlV1,
      imageUrl: task.data?.imageUrl,
      thumbnailUrlV1: task.thumbnailV1 || task.data?.thumbnailV1,
      thumbnailUrl: task.thumbnail || task.data?.thumbnail,
      showImageUrl: task.data?.uploadSoraUrl
    }
  })

  return remote || task.cover || ''
}

async function resolveLocalDisplayUrl(source?: ImageDisplaySource | null): Promise<string | null> {
  if (!source) return null

  if (isBlobUrl(source.localPreviewUrl)) {
    return source.localPreviewUrl!
  }

  if (isBlobUrl(source.previewUrl)) {
    return source.previewUrl!
  }

  if (source.localCacheKey) {
    return getCachedImageBlobUrl(source.localCacheKey)
  }

  return null
}

/** 完整展示优先级：本地 > Wasabi > S3 > ... */
export async function resolveDisplayImageUrl(
  source?: ImageDisplaySource | null
): Promise<string> {
  const remoteUrl = resolveRemoteDisplayUrl(source)

  try {
    const localUrl = await resolveLocalDisplayUrl(source)
    if (localUrl) return localUrl
  } catch (error) {
    console.warn('[image-cache] Failed to resolve local image, falling back to remote:', error)
  }

  return remoteUrl
}

/** 同步展示：仅本地 blob + 远端（不含 IndexedDB 异步读取） */
export function resolveDisplayImageUrlSync(source?: ImageDisplaySource | null): string {
  if (!source) return ''

  if (isBlobUrl(source.localPreviewUrl)) return source.localPreviewUrl!
  if (isBlobUrl(source.previewUrl)) return source.previewUrl!

  return resolveRemoteDisplayUrl(source)
}
